import apiService from "./api";
import type {
  EndUsersResponse,
  EndUserDetailsResponse,
  UserFilters,
} from "@/types";

export const endUsersApi = {
  /**
   * Get all end users (portal users from the Users table)
   * Internal admins see all users, client admins see only their client users
   */
  async getEndUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    filters?: UserFilters;
  }): Promise<EndUsersResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);

    // Add filter params as comma-separated values
    if (params?.filters?.kyc_status && params.filters.kyc_status.length > 0) {
      const kycStatusValue = params.filters.kyc_status.join(",");
      queryParams.append("kyc_status", kycStatusValue);
    }
    if (
      params?.filters?.deletion_status &&
      params.filters.deletion_status.length > 0
    ) {
      const deletionStatusValue = params.filters.deletion_status.join(",");
      queryParams.append("deletion_status", deletionStatusValue);
    }

    const query = queryParams.toString();
    const endpoint = `/clients/users/portal/users${query ? `?${query}` : ""}`;

    const response = await apiService.get(endpoint);

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Failed to fetch users" }));
      throw new Error(error.message || "Failed to fetch users");
    }

    return response.json();
  },

  /**
   * Get end user details by ID
   * Returns user with populated documents (passports)
   */
  async getEndUserDetails(userId: string): Promise<EndUserDetailsResponse> {
    const response = await apiService.get(
      `/clients/users/portal/users/${userId}`,
    );

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Failed to fetch user details" }));
      throw new Error(error.message || "Failed to fetch user details");
    }

    return response.json();
  },
};

export default endUsersApi;
